import {
  type HeaderV3Props,
  LinksCategoryId,
  type LinksData,
} from '@jouzen/ecom-components';
import cloneDeep from 'lodash/cloneDeep';
import { type DynamicConfig, useConfig } from 'statsig-react';

import useOuraGen4Enabled from '@/app/hooks/useOuraGen4Enabled';

import isPathMatchToPOP from '../utils/isPathMatchToPOP';
import useLinkModifierConfig from './useLinkModifierConfig';

interface MenuProps extends Omit<DynamicConfig, 'value'> {
  value: {
    menuLinks: HeaderV3Props['menuLinks'];
  };
}

interface ModifyPOPLinksProps {
  activeMenuLinkConfig: boolean;
  defaultLinks: HeaderV3Props['menuLinks'];
  isGen3POP: boolean;
  isPOP: boolean;
  selectedMenuLink: unknown;
}

interface ModifyPOPImageLinkProps
  extends Omit<
    ModifyPOPLinksProps,
    'activeMenuLinkConfig' | 'selectedMenuLink'
  > {
  activeMenuImageConfig: boolean;
  selectedMenuImageLink: unknown;
}

const modifyPOPLinks = ({
  activeMenuLinkConfig,
  defaultLinks,
  isGen3POP,
  isPOP,
  selectedMenuLink,
}: ModifyPOPLinksProps) => {
  if (activeMenuLinkConfig && defaultLinks && isPOP) {
    const shopCatIndex = defaultLinks.findIndex(
      ({ id }) => id === LinksCategoryId.ShopCategory,
    );
    if (shopCatIndex && shopCatIndex !== -1) {
      const links = defaultLinks[shopCatIndex]?.links;
      const targetId = isGen3POP ? 'sale' : 'oura-ring';
      const _selectedMenuLink = selectedMenuLink as LinksData['link'];

      const linksIndex = links?.findIndex(({ id }) => id === targetId);
      if (
        links &&
        typeof linksIndex !== 'undefined' &&
        linksIndex !== -1 &&
        _selectedMenuLink
      ) {
        links.splice(linksIndex, 1, _selectedMenuLink);
      }
    }
  }
};

const modifyPOPImageLink = ({
  activeMenuImageConfig,
  defaultLinks,
  isGen3POP,
  isPOP,
  selectedMenuImageLink,
}: ModifyPOPImageLinkProps) => {
  if (activeMenuImageConfig && defaultLinks && isPOP && !isGen3POP) {
    const imageIndex = defaultLinks.findIndex(
      ({ id }) => id === LinksCategoryId.MenuCard,
    );
    if (typeof imageIndex !== 'undefined' && imageIndex !== -1) {
      const _selectedMenuImageLink = selectedMenuImageLink as LinksData;
      defaultLinks.splice(imageIndex, 1, _selectedMenuImageLink);
    }
  }
};

const useMenuConfig = (pathname: string): HeaderV3Props['menuLinks'] => {
  // default menu config
  const { config } = useConfig('global_menu_links');
  const activeMenuConfig = Object.keys(config).length > 0;
  const menuConfigValue = config.value as MenuProps['value'];

  const gen4Enabled = useOuraGen4Enabled();

  // determine if current path is gen3 or OR4 POP, or neither
  const { isGen3POP, isPOP } = isPathMatchToPOP(pathname);

  // menu link modifier config for POP
  const {
    isConfigActive: activeMenuLinkConfig,
    statsigLink: selectedMenuLink,
  } = useLinkModifierConfig('pop_modified_menu_link', gen4Enabled, isGen3POP);

  // menu image modifier config for POP
  const {
    isConfigActive: activeMenuImageConfig,
    statsigLink: selectedMenuImageLink,
  } = useLinkModifierConfig(
    'pop_modified_menu_image_link',
    gen4Enabled,
    isGen3POP,
  );

  if (!activeMenuConfig) {
    return undefined;
  }

  const defaultLinks = cloneDeep(menuConfigValue.menuLinks);

  modifyPOPLinks({
    activeMenuLinkConfig,
    defaultLinks,
    isGen3POP,
    isPOP,
    selectedMenuLink,
  });

  modifyPOPImageLink({
    defaultLinks,
    activeMenuImageConfig,
    isGen3POP,
    isPOP,
    selectedMenuImageLink,
  });

  return defaultLinks;
};

export default useMenuConfig;
